// Batch loading hook for all menu item specialties
// This hook fetches all menu item specialties for a restaurant in a single query
// instead of making N queries (one per menu item)

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { isAssistantLoggedIn } from '@/lib/permissions'

export interface MenuItemSpecialty {
  specialtyId: string
  nameAr: string
  nameFr: string
  color: string
}

export const menuItemSpecialtiesKeys = {
  all: ['menu-item-specialties'] as const,
  byRestaurant: (restaurantId: string) => [...menuItemSpecialtiesKeys.all, 'restaurant', restaurantId] as const,
}

/**
 * Fetch all menu item specialties for a restaurant in a single query
 * Returns a Map<menuItemId, MenuItemSpecialty[]> for O(1) lookups
 *
 * This replaces N individual calls to useMenuItemSpecialties with a single batch call
 */
export function useAllMenuItemSpecialties(restaurantId: string | null) {
  return useQuery({
    queryKey: menuItemSpecialtiesKeys.byRestaurant(restaurantId || ''),
    staleTime: 10 * 60 * 1000, // 10 minutes - specialties don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<Map<string, MenuItemSpecialty[]>> => {
      if (!restaurantId) {
        return new Map()
      }

      // Use RPC function for batch loading - much more efficient than N+1 queries
      const rpcName = isAssistantLoggedIn()
        ? 'assistant_get_all_menu_item_specialties'
        : 'get_all_menu_item_specialties'

      // Cast to any since these RPC functions are added via migration and not in generated types yet
      const { data, error } = await (supabase.rpc as any)(rpcName, {
        p_restaurant_id: restaurantId,
      })

      if (error) {
        // Handle permission denied for assistants gracefully
        if (error.message?.includes('PERMISSION_DENIED')) {
          console.warn('Permission denied for menu item specialties')
          return new Map()
        }
        console.error('Error fetching all menu item specialties:', error)
        throw error
      }

      // Group by menu_item_id into a Map for O(1) lookups
      const specialtiesMap = new Map<string, MenuItemSpecialty[]>()

      ;(data || []).forEach((item: any) => {
        const menuItemId = item.menu_item_id
        const specialty: MenuItemSpecialty = {
          specialtyId: item.specialty_id,
          nameAr: item.name_ar,
          nameFr: item.name_fr,
          color: item.color || '#888',
        }

        const existing = specialtiesMap.get(menuItemId) || []
        specialtiesMap.set(menuItemId, [...existing, specialty])
      })

      return specialtiesMap
    },
    enabled: !!restaurantId,
  })
}

/**
 * Helper hook to get specialties for a specific menu item from the batch-loaded data
 * Use this in components that need to display specialties for a single menu item
 */
export function useMenuItemSpecialtiesFromBatch(
  specialtiesMap: Map<string, MenuItemSpecialty[]> | undefined,
  menuItemId: string | null
): MenuItemSpecialty[] {
  if (!specialtiesMap || !menuItemId) {
    return []
  }
  return specialtiesMap.get(menuItemId) || []
}
